import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Email, Person, Visibility, VisibilityOff } from '@mui/icons-material';
import { PhoneNumberInput } from '../../components/common/PhoneInput';
import axiosInstance from '../../api/axios-config';

const InviteRegistration: React.FC = () => {
  const { companyId, inviteCode } = useParams();
  const navigate = useNavigate();

  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const onSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (!companyId) {
        setError("Lien d'invitation invalide.");
        setSubmitting(false);
        return;
      }
      if (!fullname || !email || !phone || !password) {
        setError('Merci de remplir tous les champs requis.');
        setSubmitting(false);
        return;
      }
      if (password !== confirm) {
        setError('Les mots de passe ne correspondent pas.');
        setSubmitting(false);
        return;
      }

      const payload = {
        email,
        password,
        fullPhone: phone,
        fullname,
      };

      const path = inviteCode
        ? `/auth/invite-register/${companyId}/${inviteCode}`
        : `/auth/invite-register/${companyId}`;
      await axiosInstance.post(path, payload, { headers: { 'x-client-type': 'website' } });
      setSuccess(true);
      setTimeout(() => navigate('/invite/success'), 1200);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Une erreur est survenue");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Rejoindre votre entreprise
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Complétez ce formulaire pour créer votre compte et rejoindre votre organisation.
          </Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">Inscription réussie. Redirection...</Alert>}

            <TextField
              label="Nom complet"
              value={fullname}
              onChange={(e) => setFullname(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            <PhoneNumberInput
              value={phone}
              onChange={(v) => setPhone(v || '')}
              label="Téléphone"
              fullWidth
            />

            <TextField
              label="Mot de passe"
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              helperText="8 caractères minimum"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd((s) => !s)} edge="end">
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirmer le mot de passe"
              type={showConfirm ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm((s) => !s)} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={onSubmit}
              disabled={submitting}
            >
              Créer mon compte
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default InviteRegistration;
